import { IFulfillBackordersByOrderDTO, IFulfillBackordersByOrderResultDTO } from "@models/DTO/IFulfillBackordersByOrderDTO";
import IOrder from "@models/IOrder";
import React, { useState, useCallback } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import InventoryService from "@services/InventoryService";
import IGetSingleOrderBackorderAnalysisDTO from "@models/DTO/IGetSingleOrderBackorderAnalysisDTO";
import IOnGetSingleOrderBackorderAnalysisDTO from "@models/DTO/IOnGetSingleOrderBackorderAnalysisDTO";
import InventoryAllocationStrategyEnum from "@models/DTO/InventoryAllocationStrategyEnum";
import IBackorderFulfillmentOrderItemDetail from "@models/IBackorderFulfillmentOrderItemDetail";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";
import { LoadingResults } from "@components/shared/LoadingResults/LoadingResults";
import IErrorDTO from "@models/DTO/IErrorDTO";
import "./FulfillBackorderModal.scss";
import INotification from "@models/INotification";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import UIService from "@services/UIService";

type Props = {
    order: IOrder;
    closeFn: (refreshOrder?: boolean) => void;
};

export const FulfillBackorderModal = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [analysisData, setAnalysisData] = useState<IOnGetSingleOrderBackorderAnalysisDTO | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [payload, setPayload] = useState<Partial<IFulfillBackordersByOrderDTO>>({
        autoFulfill: false,
        createBackorderForRemaining: false,
        useHeldInventoryFirst: true,
    });
    const [isFulfilling, setIsFulfilling] = useState(false);
    const [fulfillmentResults, setFulfillmentResults] = useState<{
        success: boolean;
        message?: string;
        data?: IFulfillBackordersByOrderResultDTO;
    } | null>(null);
    const [showResults, setShowResults] = useState(false);

    const fulfillBackorderFn = async (payload: Partial<IFulfillBackordersByOrderDTO>) => {
        setIsFulfilling(true);
        setError(null);

        try {
            // Build fulfill payload
            const fulfillPayload: IFulfillBackordersByOrderDTO = {
                orderGuids: [props.order.guid],
                autoFulfill: payload.autoFulfill,
                createBackorderForRemaining: payload.createBackorderForRemaining,
                useHeldInventoryFirst: payload.useHeldInventoryFirst,
                allocationStrategy: InventoryAllocationStrategyEnum.FIFO,
            };

            // Call InventoryService
            const result = await InventoryService.fulfillBackordersByOrder(fulfillPayload, userModel, supplierModel);

            // Store results
            setFulfillmentResults(result);
            setShowResults(true);

            // Success notification
            const notification: INotification = {
                message: "backorder_fulfillment_success",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "An error occurred";
            const errorDTO: IErrorDTO = {
                eid: "",
                title: "error_fulfill_backorder",
                message: errorMessage,
            };
            uiContext.notifyFn(errorDTO);
            setError(errorMessage);
        } finally {
            setIsFulfilling(false);
        }
    };

    const fetchAnalysis = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const params: IGetSingleOrderBackorderAnalysisDTO = {
                orderGuid: props.order.guid,
                allocationStrategy: InventoryAllocationStrategyEnum.FIFO,
                considerReservedInventory: false,
            };

            const result = await InventoryService.getSingleOrderBackorderAnalysis(params, userModel, supplierModel);

            if (result.success && result.data) {
                setAnalysisData(result.data);
                setShowAnalysis(true);
            } else {
                setError(result.message || "Failed to fetch analysis");
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "An error occurred";
            setError(errorMessage);
            const errorDTO: IErrorDTO = {
                eid: "",
                title: "error_backorder_analysis",
                message: errorMessage,
            };
            uiContext.notifyFn(errorDTO);
        } finally {
            setIsLoading(false);
        }
    }, [props.order.guid, userModel, supplierModel, uiContext]);

    const handleAnalyze = () => {
        fetchAnalysis();
    };

    const handleFulfill = () => {
        fulfillBackorderFn(payload);
    };

    const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setPayload({ ...payload, [name]: checked });
    };

    const renderFulfillmentStatus = (item: IBackorderFulfillmentOrderItemDetail) => {
        if (item.canFullyFulfill) {
            return <span className="fulfillment-status can-fulfill">Yes</span>;
        } else if (item.suggestedQtyAllocated > 0) {
            return <span className="fulfillment-status partial-fulfill">Partial</span>;
        } else {
            return <span className="fulfillment-status cannot-fulfill">No</span>;
        }
    };

    return (
        <div className="FulfillBackorderModal">
            <div className="modal-header">
                <OwTitle title="fulfill_backorder_analysis" tag="h3" />
            </div>

            {/* Options Section - Always visible */}
            <div className="options-section">
                <OwTitle title="fulfillment_options" tag="h5" />

                <div className="option-row">
                    <OwToggle
                        label="auto_fulfill"
                        name="autoFulfill"
                        active={payload.autoFulfill || false}
                        fn={handleToggleChange}
                        labelOnRight={true}
                    />
                    <OwIconTooltip
                        iconClassName="fas fa-info-circle"
                        tooltipContent="tooltip_auto_fulfill"
                        translateContent={true}
                        inModal={true}
                    />
                </div>

                <div className="option-row">
                    <OwToggle
                        label="create_backorder_for_remaining"
                        name="createBackorderForRemaining"
                        active={payload.createBackorderForRemaining || false}
                        fn={handleToggleChange}
                        labelOnRight={true}
                    />
                    <OwIconTooltip
                        iconClassName="fas fa-info-circle"
                        tooltipContent="tooltip_create_backorder_for_remaining"
                        translateContent={true}
                        inModal={true}
                    />
                </div>

                <div className="option-row">
                    <OwToggle
                        label="use_held_inventory_first"
                        name="useHeldInventoryFirst"
                        active={payload.useHeldInventoryFirst !== undefined ? payload.useHeldInventoryFirst : true}
                        fn={handleToggleChange}
                        labelOnRight={true}
                    />
                    <OwIconTooltip
                        iconClassName="fas fa-info-circle"
                        tooltipContent="tooltip_use_held_inventory_first"
                        translateContent={true}
                        inModal={true}
                    />
                </div>
            </div>

            {/* Analyze Button - Show when no analysis yet */}
            {!showAnalysis && !isLoading && (
                <div className="action-buttons">
                    <OwButton title="analyze_order" fn={handleAnalyze} className="btn-primary" />
                    <OwButton className="btn-danger" title="cancel" fn={() => props.closeFn()} />
                </div>
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="loading-container">
                    <LoadingResults />
                </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
                <div className="error-message">
                    <p>{error}</p>
                    <OwButton title="retry" fn={handleAnalyze} />
                </div>
            )}

            {/* Analysis Results */}
            {analysisData && showAnalysis && !showResults && !isLoading && (
                <>
                    {/* Summary Section */}
                    <div className="summary-section">
                        <OwTitle title="order_summary" tag="h5" />
                        <div className="summary-row">
                            <div className="summary-col">
                                <p>
                                    <strong>Order:</strong> {analysisData.order.orderName}
                                </p>
                                <p>
                                    <strong>Account:</strong> {analysisData.order.accountName}
                                </p>
                                <p>
                                    <strong>Warehouse:</strong> {analysisData.warehouseName || "N/A"}
                                </p>
                            </div>
                            <div className="summary-col">
                                <p>
                                    <strong>Total Pending Items:</strong> {analysisData.order.totalPendingItems}
                                </p>
                                <p>
                                    <strong>Fulfillable:</strong> {analysisData.order.fulfillableItems}
                                </p>
                                <p>
                                    <strong>Partially Fulfillable:</strong> {analysisData.order.partiallyFulfillableItems}
                                </p>
                                <p>
                                    <strong>Unfulfillable:</strong> {analysisData.order.unfulfillableItems}
                                </p>
                                <p>
                                    <strong>Fulfillment %:</strong> {analysisData.order.fulfillmentPercentage.toFixed(1)}%
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Items Breakdown Table */}
                    <div className="items-table-section">
                        <OwTitle title="items_breakdown" tag="h5" />
                        <table className={"table table-sm " + (UIService.isMainViewLight() ? "" : "table-dark")}>
                            <thead>
                                <tr>
                                    <th>SKU</th>
                                    <th>Item</th>
                                    <th>Qty Pending</th>
                                    <th>Qty Available</th>
                                    <th>From Held</th>
                                    <th>Suggested</th>
                                    <th>Can Fulfill?</th>
                                    <th>%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysisData.order.items.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.sku}</td>
                                        <td>{item.name}</td>
                                        <td>{item.qtyPending}</td>
                                        <td>{item.qtyAvailable}</td>
                                        <td>{item.qtyAvailableFromHeld}</td>
                                        <td>{item.suggestedQtyAllocated}</td>
                                        <td>{renderFulfillmentStatus(item)}</td>
                                        <td>{item.fulfillmentPercentage.toFixed(0)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        <OwButton title="fulfill_order" fn={handleFulfill} className="btn-primary" disabled={isFulfilling} />
                        <OwButton className="btn-danger" title="cancel" fn={() => props.closeFn()} disabled={isFulfilling} />
                    </div>

                    {/* Fulfilling Indicator */}
                    {isFulfilling && (
                        <div className="loading-container mt-3">
                            <LoadingResults />
                            <p className="mt-2">Fulfilling backorder...</p>
                        </div>
                    )}
                </>
            )}

            {/* Fulfillment Results */}
            {showResults && fulfillmentResults && fulfillmentResults.data && (
                <div className="results-section">
                    <OwTitle title="fulfillment_results" tag="h5" />

                    {/* Summary Statistics */}
                    <div className="summary-section">
                        <div className="summary-row">
                            <div className="summary-col">
                                <p>
                                    <strong>Total Items Fulfilled:</strong> {fulfillmentResults.data.summary.totalItemsFulfilled}
                                </p>
                                <p>
                                    <strong>Items Remaining:</strong> {fulfillmentResults.data.summary.totalItemsRemaining}
                                </p>
                            </div>
                            <div className="summary-col">
                                <p>
                                    <strong>From Held Inventory:</strong> {fulfillmentResults.data.summary.inventoryUsed.fromHeld}
                                </p>
                                <p>
                                    <strong>From On-Hand Inventory:</strong> {fulfillmentResults.data.summary.inventoryUsed.fromOnHand}
                                </p>
                                <p>
                                    <strong>Total Inventory Used:</strong> {fulfillmentResults.data.summary.inventoryUsed.total}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Fully Fulfilled */}
                    {fulfillmentResults.data.fulfilledOrders.length > 0 && (
                        <div className="fulfilled-section mt-3">
                            <div className="alert alert-success">
                                <strong>Success!</strong> Order fully fulfilled.
                                {fulfillmentResults.data.fulfilledOrders[0].stateChanged && (
                                    <span>
                                        {" "}
                                        Order state changed to: <strong>{fulfillmentResults.data.fulfilledOrders[0].orderState}</strong>
                                    </span>
                                )}
                            </div>

                            {/* Items fulfilled breakdown */}
                            <OwTitle title="items_fulfilled" tag="h6" />
                            <table className={"table table-sm mt-2 " + (UIService.isMainViewLight() ? "" : "table-dark")}>
                                <thead>
                                    <tr>
                                        <th>SKU</th>
                                        <th>Item</th>
                                        <th>Qty Fulfilled</th>
                                        <th>From Held</th>
                                        <th>From On-Hand</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fulfillmentResults.data.fulfilledOrders[0].items.map((item, index: number) => (
                                        <tr key={index}>
                                            <td>{item.sku}</td>
                                            <td>{item.name}</td>
                                            <td>{item.qtyFulfilled}</td>
                                            <td>{item.fromHeldInventory}</td>
                                            <td>{item.fromOnHandInventory}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Partially Fulfilled */}
                    {fulfillmentResults.data.partiallyFulfilledOrders.length > 0 && (
                        <div className="partial-section mt-3">
                            <div className="alert alert-warning">
                                <strong>Partially Fulfilled</strong> - Some items could not be fully fulfilled.
                            </div>

                            {/* Items breakdown */}
                            <OwTitle title="items_fulfilled" tag="h6" />
                            <table className="table table-sm mt-2">
                                <thead>
                                    <tr>
                                        <th>SKU</th>
                                        <th>Item</th>
                                        <th>Qty Fulfilled</th>
                                        <th>Qty Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fulfillmentResults.data.partiallyFulfilledOrders[0].items.map((item, index: number) => (
                                        <tr key={index}>
                                            <td>{item.sku}</td>
                                            <td>{item.name}</td>
                                            <td>{item.qtyFulfilled}</td>
                                            <td>{item.qtyRemaining}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Backorder Created */}
                    {(fulfillmentResults.data.fulfilledOrders[0]?.backorderCreated ||
                        fulfillmentResults.data.partiallyFulfilledOrders[0]?.backorderCreated) && (
                        <div className="backorder-section mt-3">
                            <div className="alert alert-info">
                                <strong>Backorder Created</strong>
                                <br />
                                <span>
                                    {fulfillmentResults.data.fulfilledOrders[0]?.backorderCreated?.backorderName ||
                                        fulfillmentResults.data.partiallyFulfilledOrders[0]?.backorderCreated?.backorderName}
                                </span>
                                <br />
                                <span>
                                    Items:{" "}
                                    {fulfillmentResults.data.fulfilledOrders[0]?.backorderCreated?.itemCount ||
                                        fulfillmentResults.data.partiallyFulfilledOrders[0]?.backorderCreated?.itemCount}{" "}
                                    | Total Qty:{" "}
                                    {fulfillmentResults.data.fulfilledOrders[0]?.backorderCreated?.totalQtyPending ||
                                        fulfillmentResults.data.partiallyFulfilledOrders[0]?.backorderCreated?.totalQtyPending}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Failed */}
                    {fulfillmentResults.data.failedOrders.length > 0 && (
                        <div className="alert alert-danger mt-3">
                            <strong>Failed:</strong> {fulfillmentResults.data.failedOrders[0].reason}
                        </div>
                    )}

                    {/* Close button after viewing results */}
                    <div className="action-buttons mt-3">
                        <OwButton title="close" fn={() => props.closeFn(true)} className="btn-primary" />
                    </div>
                </div>
            )}
        </div>
    );
};
